import React, {ChangeEvent} from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import DropdownField from "./../formFields/DropdownField";
import './CustomTable.scss';
import TextField from '../formFields/TextField';
import {getComparator} from './AccordionTable'
import TransactionDocument, { ModifiableValue } from '../../models/document';
import {connect} from 'react-redux';
import FeeSchedule from '../../models/feeSchedule';
import {TransactionDetails} from "../../models/transaction";
import { AdministrationLevel } from '../../models/misc';

interface Data {
    record_name: string;
    source: string;
    sab: string;
    size: string;
    pages: number;
    actions: string;
}

type Order = 'asc' | 'desc';

function stableSort<T>(array: readonly T[], comparator: (a: T, b: T) => number) {
    const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) {
            return order
        }

        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
    id: keyof Data;
    label: string;
}

const headCells: HeadCell[] = [
    {id: 'record_name', label: 'RECORD NAME'},
    {id: 'source', label: 'SOURCE'},
    {id: 'sab', label: 'S/A/B'},
    {id: 'size', label: 'SIZE'},
    {id: 'pages', label: 'PAGES'},
    {id: 'actions', label: 'ACTIONS'},
];

interface EnhancedTableProps {
    onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
    order: Order;
    orderBy: string;
    hideSource?: Boolean
}

export class AuxDocumentErrors {
    constructor(state: string, feeSchedule: string) {
        this.state = state;
        this.feeSchedule = feeSchedule;
    }

    state: string;
    feeSchedule: string;

    isValid() {
        return !this.state && !this.feeSchedule;
    }
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const {order, orderBy, onRequestSort} = props;

    const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };
    let updatedHeadCells = [...headCells]
    if (props.hideSource) {
        updatedHeadCells.splice(1, 1)
    }
    return (
        <TableHead>
            <TableRow>
                {updatedHeadCells.map((headCell) => (
                    <TableCell
                        colSpan={4}
                        key={headCell.id}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

interface IpropTypes {
    rows: readonly TransactionDocument[],
    getDocumentById: Function,
    setShowRecordGrid: any
    hideSource?: boolean,
    setSelectedMedDocumentIndex?: any,
    updateDocument: Function,
    transactionId: string,
    documentSources?: any,
    deleteDocument: Function,
    getTransactionDocuments: any,
    auditOnShowDoc: boolean,
    transactionDetails?: TransactionDetails,
    feeSchedules?: readonly FeeSchedule[],
    isEditing: boolean,
    changeSubRowData: (documentId: string, value: string, key: ModifiableValue) => void,
    errors: readonly AuxDocumentErrors[],
    isMultiState: boolean,
    billingStates: readonly AdministrationLevel[],
}

function RecordsTable(props: IpropTypes) {
    const [order, setOrder] = React.useState<Order>('asc');
    const [orderBy, setOrderBy] = React.useState<keyof Data>('pages');

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const removeDocument = (id: any) => {
        if (window.confirm("Are you certain you wish to delete this file?")) {
            props.deleteDocument(id)
            props.getTransactionDocuments(props.transactionId)
        }
    }

    const medAPIFetch = (documentId: string) => {
        props.setShowRecordGrid(false)
        props.setSelectedMedDocumentIndex(documentId)
        props.getDocumentById(documentId, props.auditOnShowDoc)
        return ""
    }

    return (
        <div className="records-table-main" style={props.hideSource ? {width: "100%"} : {}}>
            <Table
                size="small" stickyHeader className='records-table'
            >
                <div className="records-table-inner">
                    <EnhancedTableHead
                        order={order}
                        orderBy={orderBy}
                        onRequestSort={handleRequestSort}
                        hideSource={props.hideSource}
                    />
                    <TableBody>
                        {stableSort(props.rows, getComparator(order, orderBy)).map((row, index) => <>
                            <TableRow
                                hover
                                key={row[index]}
                            >
                                <TableCell className='record_name' colSpan={4}>
                                    {props.isEditing ? <TextField
                                        className="edit-textinput"
                                        label=""
                                        value={row.description}
                                        onChange={(value) => {
                                            props.changeSubRowData(row.id, value, ModifiableValue.Description)
                                        }}
                                        errMsg=""
                                        placeholder=""
                                    /> : <a
                                        href="#!"
                                        onClick={() => medAPIFetch(row.id)}
                                        className="records-table-file"
                                        style={row.description ? {} : {opacity: 0.5}}
                                    >
                                        {row.description || "No file name"}
                                    </a>}
                                </TableCell>
                                {props.hideSource ? null : <TableCell className='record_source'>
                                    <DropdownField
                                        required={true}
                                        options={props.documentSources}
                                        onChange={(e) => {
                                            props.changeSubRowData(row.id, e.target.value, ModifiableValue.Source)
                                        }}
                                        value={row.sourceID?.toString() || "34000"}
                                        meta='abc'
                                    />
                                </TableCell>}
                                <TableCell className='record_sab' colSpan={4}>{row.sab}</TableCell>
                                <TableCell className='record_size' colSpan={4}>{row.size ? (row.size / (1024 * 1024)).toFixed(2) + " MB" : ""}</TableCell>
                                <TableCell className='record_pages' colSpan={4}>{row.pageCount}</TableCell>
                                <TableCell className='record_remove'>
                                    <div className='record_options_items'>
                                        <span
                                            className='remove_doc'
                                            onClick={() => removeDocument(row.id)}
                                            style={{display: 'flex', alignItems: 'center', marginTop: 1}}
                                        >
                                            <svg width="15" height="15" viewBox="0 0 1200 1200" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M600 0C268.629 0 0 268.629 0 600C0 931.371 268.629 1200 600 1200C931.371 1200 1200 931.371 1200 600C1200 268.629 931.371 0 600 0ZM411.475 262.5L600 451.025L788.525 262.5L937.5 411.475L748.975 600L937.5 788.525L788.525 937.5L600 748.975L411.475 937.5L262.5 788.525L451.025 600L262.5 411.475L411.475 262.5Z" fill="#CC5967"/>
                                            </svg>
                                            <span className='record_remove_span'>Remove</span>
                                        </span>
                                    </div>
                                </TableCell>
                            </TableRow>
                            {props.isEditing && <>
                                <TableRow key={`medret1${row[index]}`} className="secondrow">
                                    <TableCell colSpan={5} style={{width: "25%"}}>
                                        {props.isMultiState && <DropdownField
                                            label="State"
                                            options={[
                                                {label: "None", value: "", disabled: true, hidden: true},
                                                ...props.billingStates.map((state: any) => {
                                                    return {label: state.name, value: state.abbreviation}
                                                })
                                            ]}
                                            onChange={(e) => {
                                                props.changeSubRowData(row.id, e.target.value, ModifiableValue.State)
                                            }}
                                            input={{value: row.state ?? ""}}
                                            meta="state"
                                            key={row.id + row.state + row.feeScheduleId}
                                        />}
                                    </TableCell>
                                    <TableCell colSpan={5} style={{width: "25%"}}>
                                        {props.isMultiState && <DropdownField
                                            label="Fee Schedule"
                                            required={true}
                                            options={[
                                                {label: "- Select -", value: "", disabled: true, hidden: true},
                                                ...props.feeSchedules!.filter(fee => {
                                                    return !fee.state || fee.state == row.state
                                                }).map(fee => {
                                                    return {label: fee.name, value: fee.id}
                                                }),
                                            ]}
                                            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                                                props.changeSubRowData(row.id, event.target.value, ModifiableValue.FeeSchedule)
                                            }}
                                            errMsg={props.errors[index].feeSchedule}
                                            value={row.feeScheduleId ?? ""}
                                            meta="feeScheduleId"
                                            key={row.id + row.state + row.feeScheduleId}
                                        />}
                                    </TableCell>
                                    <TableCell colSpan={5} style={{width: "25%"}}></TableCell>
                                    <TableCell colSpan={5} style={{width: "25%"}}></TableCell>
                                </TableRow>
                            </>}
                        </>)}
                    </TableBody>
                </div>
            </Table>
        </div>
    );
}

function mapStateToProps(state: any) {
    return {
        deleteDocumentSuccess: state.dataEntryState.deleteDocumentAPISuccess,
        transactionDetails: state.dataEntryState.transactionDetails,
        feeSchedules: state.dataEntryState.feeSchedules,
    };
}

export default connect<{}, {}, IpropTypes>(
    mapStateToProps,
)(RecordsTable);
